/**
 * Live cache-optimizer progress for the flow canvas.
 *
 * Subscribes to the optimizer job's event stream (`GET /jobs/:jobId/stream`,
 * the same SSE transport workflow runs use) and reduces its events into a
 * per-node chip state. The stream replays cached events on connect, so a
 * late-joining or remounting client recovers in-flight state.
 *
 * `jobId` is resolved by the caller — from the manual "Optimize cache" mutation
 * result, or from `GET /workflows/:id/optimizer-status` for auto runs.
 *
 * @module hooks/useCacheOptimizerStream
 */

import React from 'react';
import type {
  CacheOptimizerNodeData,
  CacheOptimizerProgressData,
} from '@chatsdet/types';
import { API_URL } from '../config';

/** Chip state rendered on a canvas node. `optimizing` is derived, not emitted. */
export type OptimizerChipStatus =
  | 'optimizing'
  | 'optimized'
  | 'partial'
  | 'failed'
  | 'skipped';

export interface OptimizerNodeChip {
  status: OptimizerChipStatus;
  cachedSteps?: number;
  gapSteps?: number;
  reason?: string;
}

export interface CacheOptimizerStreamState {
  /** True while the optimizer job is running (a chip layer should show). */
  isOptimizing: boolean;
  /** nodeId → chip. Empty when idle. */
  nodeStatuses: Map<string, OptimizerNodeChip>;
  /** Tallies once the job completes; null while running/idle. */
  summary: { committed: number; failed: number } | null;
}

export type CacheOptimizerAction =
  | { kind: 'reset' }
  | { kind: 'started'; data: CacheOptimizerProgressData }
  | { kind: 'node'; data: CacheOptimizerNodeData }
  | { kind: 'complete'; data: CacheOptimizerProgressData }
  | { kind: 'terminated' };

interface InternalState extends CacheOptimizerStreamState {
  /** Nodes announced at `started` — used to resolve leftovers to 'skipped'. */
  committable: Set<string>;
}

export const EMPTY_OPTIMIZER_STATE: InternalState = {
  isOptimizing: false,
  nodeStatuses: new Map(),
  summary: null,
  committable: new Set(),
};

export function cacheOptimizerReducer(
  state: InternalState,
  action: CacheOptimizerAction
): InternalState {
  switch (action.kind) {
    case 'reset':
      return EMPTY_OPTIMIZER_STATE;
    case 'started': {
      const committable = new Set(action.data.nodeIds ?? []);
      const nodeStatuses = new Map<string, OptimizerNodeChip>();
      for (const nodeId of committable) nodeStatuses.set(nodeId, { status: 'optimizing' });
      return { isOptimizing: true, nodeStatuses, summary: null, committable };
    }
    case 'node': {
      const nodeStatuses = new Map(state.nodeStatuses);
      nodeStatuses.set(action.data.nodeId, {
        status: action.data.status,
        cachedSteps: action.data.cachedSteps,
        gapSteps: action.data.gapSteps,
        reason: action.data.reason,
      });
      return { ...state, nodeStatuses };
    }
    case 'complete':
    case 'terminated': {
      // Any committable node still showing the spinner was never authored —
      // resolve it to skipped so the canvas never sticks on "optimizing".
      const nodeStatuses = new Map(state.nodeStatuses);
      for (const nodeId of state.committable) {
        const chip = nodeStatuses.get(nodeId);
        if (!chip || chip.status === 'optimizing') nodeStatuses.set(nodeId, { status: 'skipped' });
      }
      const summary =
        action.kind === 'complete'
          ? { committed: action.data.committed ?? 0, failed: action.data.failed ?? 0 }
          : state.summary;
      return { ...state, isOptimizing: false, nodeStatuses, summary };
    }
    default:
      return state;
  }
}

export function useCacheOptimizerStream(opts: {
  jobId: string | null;
}): CacheOptimizerStreamState {
  const { jobId } = opts;
  const [state, dispatch] = React.useReducer(cacheOptimizerReducer, EMPTY_OPTIMIZER_STATE);

  React.useEffect(() => {
    if (!jobId) {
      dispatch({ kind: 'reset' });
      return;
    }
    // New job — clear any prior result before this stream's replay arrives.
    dispatch({ kind: 'reset' });

    const eventSource = new EventSource(`${API_URL}/jobs/${jobId}/stream`, {
      withCredentials: true,
    });

    const onProgress = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as CacheOptimizerProgressData;
        if (data.phase === 'started') dispatch({ kind: 'started', data });
        else if (data.phase === 'complete') dispatch({ kind: 'complete', data });
      } catch {
        /* ignore malformed frame */
      }
    };
    const onNode = (e: MessageEvent) => {
      try {
        dispatch({ kind: 'node', data: JSON.parse(e.data) as CacheOptimizerNodeData });
      } catch {
        /* ignore malformed frame */
      }
    };
    // Job lifecycle terminal states — finalize even if the optimizer's own
    // 'complete' event was dropped (e.g. the job crashed before emitting it).
    const onStatus = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as { status?: string };
        if (data.status === 'completed' || data.status === 'failed' || data.status === 'canceled') {
          dispatch({ kind: 'terminated' });
          eventSource.close();
        }
      } catch {
        /* ignore */
      }
    };

    eventSource.addEventListener('cache-optimizer-progress', onProgress);
    eventSource.addEventListener('cache-optimizer-node', onNode);
    eventSource.addEventListener('status', onStatus);
    eventSource.addEventListener('error', () => {
      // Transient disconnects auto-reconnect; a hard error just stops updates.
    });

    return () => eventSource.close();
  }, [jobId]);

  return {
    isOptimizing: state.isOptimizing,
    nodeStatuses: state.nodeStatuses,
    summary: state.summary,
  };
}
